import { Component, Input, OnInit, computed, signal } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { IonLabel, IonItem, IonThumbnail, IonSkeletonText, IonList, IonListHeader, ToastController, IonCardSubtitle } from '@ionic/angular/standalone';
import { StandardInsight } from '../../services/insights.service';
import { IAsset } from '../../_models/asset.model';
import { DataService } from '../../services/data.service';
import { TagCacheService } from '../../services/tag-cache.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HistoricalInsightsService, InsightScope, IAlarmHistoryRecord, IStandardHistoricalRecord, IDurationHistoricalRecord } from '../../services/historical-insights.service';
import _ from 'lodash';
import { DecimalPlacesPipe } from '../../pipes/decimal-places.pipe';
import { ChunkprogressbarInsightComponent } from '../chunkprogressbar-insight/chunkprogressbar-insight.component';

@Component({
  selector: 'lib-historical-average-insight',
  standalone: true,
  imports: [
    IonItem, IonLabel, CommonModule, IonCardSubtitle,
    IonSkeletonText, IonThumbnail, IonList, IonListHeader,
    ChunkprogressbarInsightComponent,
  ],
  templateUrl: './historical-average-insight.component.html',
  styleUrls: ['./historical-average-insight.component.css'],
  providers: [DecimalPlacesPipe, DecimalPipe]
})
export class HistoricalAverageInsightComponent implements OnInit {

  public loading = signal(true);
  public loaded = computed(() => !this.loading());
  public groupedData: any = null;

  @Input() insight!: StandardInsight;
  @Input() id!: number | undefined;
  @Input() type: InsightScope = 'asset';
  @Input() assets: IAsset[] = [];

  public liveTagIds: number[] = [];
  public startEpoch: number = Date.now() - 3600000;
  public endEpoch: number = Date.now();

  public records: IAlarmHistoryRecord[] | IStandardHistoricalRecord[] | IDurationHistoricalRecord[] = [];
  public uniqueIdentifier: string = '';
  public dataValues: any[] = [];
  public values: any[] = [];
  public value: number = 0;

  constructor(
    private dataService: DataService,
    public tagCacheService: TagCacheService,
    private router: Router,
    private route: ActivatedRoute,
    private toastCtrl: ToastController,
    private decimalPlacesPipe: DecimalPlacesPipe,
    private historicalInsightsService: HistoricalInsightsService
  ) {
            this.uniqueIdentifier = Math.random().toString(36).substring(2, 15);

  }

  
  ngOnInit() {

    //this.utilityService.titleMenuChange$.next('Tag Chart');

    this.route.queryParamMap.subscribe(async params => {

      this.initializeLiveTagIds();
      this.processTimeRange(this.insight.statisticalConfig?.timeRange)

      this.dataService.GetObservationAggregatedHighChartValuesWithEpochTime(this.liveTagIds, this.startEpoch, this.endEpoch)
                      .subscribe({        
        next:(data: any) => {
          this.performAnalysis(data)
          this.loading.set(false);
        },
        error:(err: any) => {
          console.error('Error loading data', err);
        },
      })
    });
  }

  processTimeRange(timeRange: any) {
    this.endEpoch = Date.now();

    switch (timeRange) {
      case '1h':
        this.startEpoch = this.endEpoch - (60 * 60 * 1000); // 60 minutes in milliseconds
        break;
      case '24h':
        this.startEpoch = this.endEpoch - (24 * 60 * 60 * 1000); // 24 hours in milliseconds
        break;
      case '7d':
        this.startEpoch = this.endEpoch - (7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
        break;
      default:
        // Handle cases where no time window is specified or an invalid value is provided
        console.warn('[HistoricalAverageInsightComponent] Invalid or undefined time range', timeRange);
        break;
    }
  }

  processSparklineData(seriesData: { data: any[] }) {
    // Create a Map to store unique timestamps
    const uniquePointsMap = new Map<number, number>();
  
    // Iterate through data in reverse to keep the last occurrence
    for (let i = seriesData.data.length - 1; i >= 0; i--) {
      const point = seriesData.data[i];
      const [timestamp, value] = point;
  
      // Only add if timestamp not already in map
      if (!uniquePointsMap.has(timestamp)) {
        uniquePointsMap.set(timestamp, value);
      }
    }
  
    // Convert back to array, sorted by timestamp
    var uniquePoints = Array.from(uniquePointsMap.entries())
      .sort((a, b) => a[0] - b[0]);

    return uniquePoints;
  }
  
  performArrayOperation(data: any[], operationType: string) {
    // Validate input
    if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Input must be a non-empty array');
    }

    // Ensure all elements are numbers
    const numericData = data.filter(item => typeof item === 'number');
    
    switch (operationType.toLowerCase()) {
        case 'average':
            return this.calculateAverage(numericData);
        
        case 'median':
            return this.calculateMedian(numericData);
        
        case 'mode':
            return this.calculateMode(numericData);
        
        case 'min':
            return Math.min(...numericData);
        
        case 'max':
            return Math.max(...numericData);
        
        case 'range':
            return this.calculateRange(numericData);
          
        case 'variance':
            return this.calculateVariance(numericData);

        case 'standard deviation':
            return this.calculateStandardDeviation(numericData);

        default:
            throw new Error('Invalid operation type. Supported types: average, median, mode, min, max');
    }
}

// calculate average
calculateAverage(arr: any[]) {
    return arr.reduce((sum, val) => sum + val, 0) / arr.length;
}

// calculate median
calculateMedian(arr: any[]) {
    const sorted = arr.slice().sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    
    return sorted.length % 2 !== 0 
        ? sorted[middle] 
        : (sorted[middle - 1] + sorted[middle]) / 2;
}

// calculate mode
calculateMode(arr: number[]): number {
  const frequency: { [key: number]: number } = {}; // Define frequency as an object with number keys
  let maxCount = 0;
  let modes: number[] = [];

  // Count the occurrences of each number
  arr.forEach(num => {
      frequency[num] = (frequency[num] || 0) + 1;

      // Check if this count is the highest
      if (frequency[num] > maxCount) {
          maxCount = frequency[num];
      }
  });

  // Find all numbers that occur with the maximum count
  for (const num in frequency) {
      if (frequency[num] === maxCount) {
          modes.push(Number(num)); // Convert string key back to number
      }
  }

  return modes[0];
}

calculateRange(arr: number[]): number {
  return Math.max(...arr) - Math.min(...arr);
}

calculateVariance(arr: number[]): number {
  const mean = this.calculateAverage(arr);
  const squaredDiffs = arr.map(val => Math.pow(val - mean, 2));
  return squaredDiffs.reduce((sum, val) => sum + val, 0) / arr.length;
}

calculateStandardDeviation(arr: number[]): number {
  return Math.sqrt(this.calculateVariance(arr));
}

  performAnalysis(data: any) {
    const seriesData = this.formatChartData(data);

    seriesData.forEach((d: any) => {
       // extract data values for sparkline
      var uniquePoints = this.processSparklineData(d)

      // extract just the values
      this.dataValues = uniquePoints.map( (point:any) => point[1]);

      // make computations
      console.log("[HistoricalAverageInsightComponent] data values", this.dataValues)
      var configIndex = this.insight.statisticalConfig?.compareConfig?.findIndex((cc: any) => d.JBTStandardObservationId == cc.tagId) ?? 0

      if(configIndex == -1) {
        return;
      }
      
      if(this.insight.statisticalConfig?.compareConfig) {
        // perform operation for compare config
        this.values[configIndex] = this.performArrayOperation(this.dataValues, this.insight.statisticalConfig?.compareConfig[configIndex].operation as string)
      }
      else {
        this.values[configIndex] = this.performArrayOperation(this.dataValues, this.insight.statisticalConfig?.operation as string)
        this.value = this.values[0]

      }
    })

    if(this.insight.statisticalConfig?.compareConfig) {
      // compare values
      if(this.insight.statisticalConfig?.comparisonType == 'percentage') {
        this.value = ( this.values[0] / this.values[1] ) * 100
      }
      else if(this.insight.statisticalConfig?.comparisonType == 'difference') {
        this.value = this.values[0] - this.values[1]
      }
      else {
        this.value = this.values[0] / this.values[1]
      }
    }

  }

  private initializeLiveTagIds(): void {
    if (!this.insight.ids?.length) {
      console.warn('[HistoricalAverageInsightComponent] Missing standard observation IDs.');
      this.loading.set(false);
      return;
    }

    const assetId = this.id ?? 0

    // fetch the tag for asset and standard
    var allTagsForAssetId = this.tagCacheService.getTagsForAsset(assetId)
    this.liveTagIds = allTagsForAssetId()
                      .where((t: any) => this.insight.ids?.includes(t.JBTStandardObservationId) as boolean)
                      .select((t: any) => t.Id)
                      .toArray();

    this.loading.set(false);
  }

  private formatChartData(raw: any[]): any[] {
    let seriesCounter = 0;

    const GraphTagsData = raw.reduce((o, cur) => {
      const occurs = o.findIndex((item: any) => item.Tag.Id === cur.TagId);
      if (occurs >= 0) {
        o[occurs].TagValues += cur.TagValues;
      } else {
        o.push({
          Tag: {
            Name: cur.tagName,
            Id: cur.TagId,
            JBTStandardObservationId: cur.JBTStandardObservationId,
            Asset: {
              Name: cur.AssetName,
              Site: { Name: cur.SiteName },
            },
          },
          JBTStandardObservationName: cur.JBTStandardObservationName,
          GateName: cur.GateName,
          TagValues: cur.TagValues,
        });
      }
      return o;
    }, []);

    GraphTagsData.forEach((graphTag: any) => {
      graphTag.AggregateObservations = graphTag.TagValues
        .split('|')
        .filter((v: any) => v.length > 5)
        .map((pair: any) => {
          const [t, v] = pair.split(',');
          return [+t, +v || 0];
        });
    });

    const seriesData = GraphTagsData.map((gt: any) => {
      const dataPairs = gt.AggregateObservations || [];
      gt.isDigital = this.isDataDigital(dataPairs);

      const obs = this.dataService.cache.jbtStandardObservationsObject?.[gt.Tag.JBTStandardObservationId];
      let nameString = obs
        ? `${gt.Tag.Asset.Site.Name} ${gt.GateName} ${gt.Tag.Asset.Name} ${obs.Name}`
        : gt.Tag.Name;

      const seriesIndex = seriesCounter++;

      return {
        id: String(gt.Tag.Id),   // 👈 add this
        seriesNumber: seriesIndex,
        connectNulls: true,
        tagId: gt.Tag.Id,
        AssetId: gt.Tag.Asset?.Id,            // 👈 add if you have it
        Asset: gt.Tag.Asset.Name,
        longTagName: gt.Tag.Name,
        isDigital: gt.isDigital,
        name: nameString,
        JBTStandardObservationId: obs?.Id ?? '',
        jbtStandardObservationName: obs?.Name ?? '',
        data: dataPairs
          .map((point: any) => [
            point[0],
            gt.isDigital
              ? point?.[1] || 0
              : this.decimalPlacesPipe.transform(point[1], 1, gt.Tag.JBTStandardObservationId)
          ]),
        //color: '',
        step: 'left'
      };
    });

    return seriesData;
  }

  private isDataDigital(observations: any): boolean {
    const distinctDataRough = _.take(observations, 500);
    const distinctData = _.uniqBy(distinctDataRough, (a: any, b: any) => {
      return (a && (a[1] || 0)) == (b && (b[1] || 0));
    });
    const dataArr = distinctData as Array<[number, number]>;
    return dataArr.length === 2 && dataArr[0][1] + dataArr[1][1] === 1;
  }

  ngOnDestroy() {

  }

}
